import {AxiosError} from 'axios'

export interface AccountingApiError {
  success: false
  error: string
  errorCode?: string
  status?: number
  data?: unknown
}

type LocalizedMessage = {
  en?: string
  fr?: string
  ar?: string
}

export function pickAccountingMessage(message: unknown, locale: 'en' | 'fr' = 'fr'): string {
  if (!message) return ''
  if (typeof message === 'string') return message
  if (typeof message === 'object') {
    const localized = message as LocalizedMessage
    return localized[locale] || localized.fr || localized.en || ''
  }
  return ''
}

export function parseAccountingApiError(error: unknown, locale: 'en' | 'fr' = 'fr'): AccountingApiError {
  const err = error as AxiosError<any> & {message?: string}
  const status = err.response?.status
  let data = err.response?.data
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    data = undefined
  }
  const axiosCode = typeof (err as {code?: string}).code === 'string' ? (err as {code: string}).code : ''
  const timedOut =
    axiosCode === 'ECONNABORTED' ||
    axiosCode === 'ETIMEDOUT' ||
    status === 408 ||
    /timeout/i.test(String(err.message || ''))
  const errorCode =
    typeof data?.errorCode === 'string'
      ? data.errorCode
      : timedOut && !data
        ? 'REQUEST_TIMEOUT'
        : undefined
  let message = pickAccountingMessage(data?.message, locale)
  if (!message && typeof data?.error === 'string') message = data.error
  if (!message && err instanceof Error) message = err.message
  if (status === 401) message = message || 'Authentication required'
  if (status === 403) message = message || 'Access forbidden'
  if (status === 404) message = message || 'Not found'
  if (timedOut && !data) message = message || 'Request timed out'
  return {
    success: false,
    error: message || 'Accounting request failed',
    errorCode: errorCode || (timedOut && !data ? 'REQUEST_TIMEOUT' : undefined),
    status: timedOut && !data ? status || 408 : status,
    data,
  }
}
